import { createFileRoute, useNavigate } from '@tanstack/react-router'

import type { InventoryLevelDetail } from '@/api/admin-inventory-types'
import { useAdjustInventoryLevel, useUpdateInventoryLevel } from '@/api/api-mutations'
import { preloadGetInventoryLevel } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'
import { PenPlus } from '@/icons/pen-plus'

export const Route = createFileRoute('/_authenticated/_general/inventory/$inventoryLevelUuid/adjust')({
	component: AdjustInventoryLevelRoute,
	loader: async ({ context, params }) => {
		await preloadGetInventoryLevel(context, { path: { inventoryLevelUuid: params.inventoryLevelUuid } })
	},
	staticData: {
		crumb: ({ context: { L } }) => L.Inventory.Adjust()
	}
})

function AdjustInventoryLevelRoute() {
	const { inventoryLevelUuid } = Route.useParams()

	return (
		<RouteModal
			prev='/inventory'
			asDrawer
			requiresData
			loader={async api => api.getInventoryLevel({ path: { inventoryLevelUuid } })}
		>
			{data => <AdjustInventoryLevelForm level={data} />}
		</RouteModal>
	)
}

function AdjustInventoryLevelForm({ level }: { level: InventoryLevelDetail }) {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const navigate = useNavigate()
	const adjustMutation = useAdjustInventoryLevel(true)
	const updateLevelMutation = useUpdateInventoryLevel(true)

	const form = useAppForm({
		defaultValues: {
			adjustDelta: '',
			adjustReason: '',
			reorderPoint: level.reorderPoint != null ? String(level.reorderPoint) : '',
			reorderQuantity: level.reorderQuantity != null ? String(level.reorderQuantity) : ''
		},
		onSubmit: async ({ value }) => {
			const parsedReorderPoint = value.reorderPoint === '' ? undefined : Number(value.reorderPoint)
			const parsedReorderQuantity = value.reorderQuantity === '' ? undefined : Number(value.reorderQuantity)
			const reorderChanged =
				parsedReorderPoint !== (level.reorderPoint ?? undefined) ||
				parsedReorderQuantity !== (level.reorderQuantity ?? undefined)

			const deltaValue = value.adjustDelta === '' ? 0 : Number(value.adjustDelta)
			if (value.adjustDelta !== '' && Number.isNaN(deltaValue)) {
				toast.error(L.General.Required())
				return
			}
			const hasDelta = deltaValue !== 0

			if (!reorderChanged && !hasDelta) {
				toast.info(L.Inventory.AdjustNoChanges())
				return
			}

			if (reorderChanged) {
				await updateLevelMutation.mutateAsync({
					path: { inventoryLevelUuid: level.uuid },
					body: { reorderPoint: parsedReorderPoint, reorderQuantity: parsedReorderQuantity }
				})
			}

			if (hasDelta) {
				await adjustMutation.mutateAsync({
					path: { inventoryLevelUuid: level.uuid },
					body: { quantityDelta: deltaValue, reason: value.adjustReason || undefined }
				})
			}

			toast.success(L.Inventory.AdjustSuccess())
			handleSuccess()
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Inventory.Adjust()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<div className='mb-4 space-y-1'>
						<div className='flex items-center justify-between gap-2'>
							<p className='font-medium text-ui-fg-base'>{level.variant.productTitle}</p>
							<Button
								size='small'
								variant='transparent'
								type='button'
								className='shrink-0 gap-x-1'
								onClick={() =>
									void navigate({
										to: '/products/$productUuid',
										params: { productUuid: level.variant.productUuid }
									})
								}
							>
								<PenPlus />
								{L.Inventory.EditProduct()}
							</Button>
						</div>
						{level.variant.sku && (
							<p className='text-sm text-ui-fg-muted'>
								{L.Inventory.Column.Sku()}: {level.variant.sku}
							</p>
						)}
						<p className='text-sm text-ui-fg-muted'>
							{L.Inventory.Column.InventoryLocation()}: {level.inventoryLocation.name}
						</p>
						<p className='text-sm text-ui-fg-base'>
							{L.Inventory.Column.QuantityOnHand()}:{' '}
							<span className='font-medium'>{level.quantityOnHand}</span>
						</p>
					</div>
					<FormFieldGroup>
						<form.AppField
							name='adjustDelta'
							children={field => (
								<field.TextField label={L.Inventory.Column.Delta()} optional type='number' step='any' />
							)}
						/>
						<Text className='-mt-1 text-xs text-ui-fg-subtle'>{L.Inventory.AdjustDeltaHint()}</Text>
						<form.AppField
							name='adjustReason'
							children={field => <field.TextField label={L.Inventory.Column.Reason()} optional />}
						/>
						<div className='border-t border-ui-border-base pt-4'>
							<p className='mb-3 text-sm font-medium text-ui-fg-subtle'>
								{L.Inventory.ReorderSettings()}
							</p>
							<form.AppField
								name='reorderPoint'
								children={field => (
									<field.TextField
										label={L.Inventory.Column.ReorderPoint()}
										optional
										type='number'
										step='any'
									/>
								)}
							/>
							<form.AppField
								name='reorderQuantity'
								children={field => (
									<field.TextField
										label={L.Inventory.Column.ReorderQuantity()}
										optional
										type='number'
										step='any'
									/>
								)}
							/>
						</div>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex w-full items-center justify-between gap-x-2'>
					<Button
						variant='secondary'
						size='small'
						type='button'
						onClick={() =>
							navigate({
								to: '/inventory/movements',
								search: {
									variantUuid: level.variant.uuid,
									inventoryLocationUuid: level.inventoryLocation.uuid
								}
							})
						}
					>
						{L.Inventory.ViewMovements()}
					</Button>
					<div className='flex items-center gap-x-2'>
						<Drawer.Close asChild>
							<Button variant='secondary' size='small' type='button'>
								{L.General.Cancel()}
							</Button>
						</Drawer.Close>
						<form.SaveButton />
					</div>
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
